import { useState, useEffect } from "react";
import { Share2, MessageCircle, Link2, Send, Instagram } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import ShareCard from "@/components/ShareCard";
import { Capacitor } from "@capacitor/core";
import { supabase } from "@/lib/supabase";

export default function ShareButton({
  title,
  listingId,
  category = "",
  image = "",
  className = "",
}: {
  title: string;
  listingId?: string;
  category?: string;
  image?: string;
  className?: string;
}) {
  const { toast } = useToast();
  const [cardOpen, setCardOpen] = useState(false);
  const [showStories, setShowStories] = useState(false);
  const origin = "https://www.agroaluga.com.br";
  const shareText = `${title} - AgroAluga`;

  useEffect(() => {
    let nativo = false;
    try { nativo = Capacitor.isNativePlatform(); } catch { nativo = false; }
    let mobile = false;
    try {
      const ua = navigator.userAgent || "";
      const porUA = /Android|iPhone|iPad|iPod|Mobile|Opera Mini|IEMobile/i.test(ua);
      const porTouch =
        (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(pointer: coarse)").matches) ||
        (typeof window !== "undefined" && window.innerWidth <= 820);
      mobile = porUA || porTouch;
    } catch { mobile = false; }
    setShowStories(nativo || mobile);
  }, []);

  const fallbackUrl = listingId
    ? `${origin}/anuncio/${listingId}`
    : (typeof window !== "undefined" ? window.location.href : "");

  async function resolveShareUrl(): Promise<string> {
    if (!listingId) return fallbackUrl;
    try {
      const { data, error } = await supabase
        .from("listings")
        .select("short_code")
        .eq("id", listingId)
        .maybeSingle();
      if (error || !data?.short_code) return fallbackUrl;
      return `${origin}/a/${data.short_code}`;
    } catch {
      return fallbackUrl;
    }
  }

  async function openWhatsApp(e: React.MouseEvent) {
    e.preventDefault();
    const url = await resolveShareUrl();
    const msg = encodeURIComponent(`${shareText}\n${url}`);
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  }

  async function copyLink(e: React.MouseEvent) {
    e.preventDefault();
    const url = await resolveShareUrl();
    try {
      await navigator.clipboard.writeText(url);
      toast({ description: "Link copiado para a area de transferencia." });
    } catch {
      toast({ description: "Nao foi possivel copiar o link.", variant: "destructive" });
    }
  }

  async function nativeShare(e: React.MouseEvent) {
    e.preventDefault();
    const url = await resolveShareUrl();
    if (navigator.share) {
      try {
        await navigator.share({ title: shareText, text: shareText, url });
      } catch { /* cancelou */ }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        toast({ description: "Link copiado para a area de transferencia." });
      } catch {
        toast({ description: "Nao foi possivel compartilhar.", variant: "destructive" });
      }
    }
  }

  function openCard(e: React.MouseEvent) {
    e.preventDefault();
    setCardOpen(true);
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className={`p-1.5 rounded-full text-muted-foreground hover:text-primary transition-colors ${className}`}
            aria-label="Compartilhar anuncio"
          >
            <Share2 className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          {showStories && (
            <DropdownMenuItem onClick={openCard} className="cursor-pointer">
              <Instagram className="h-4 w-4 mr-2 text-pink-600" />
              Instagram / Stories
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={openWhatsApp} className="cursor-pointer">
            <MessageCircle className="h-4 w-4 mr-2 text-green-600" />
            WhatsApp
          </DropdownMenuItem>
          <DropdownMenuItem onClick={copyLink} className="cursor-pointer">
            <Link2 className="h-4 w-4 mr-2" />
            Copiar link
          </DropdownMenuItem>
          <DropdownMenuItem onClick={nativeShare} className="cursor-pointer">
            <Send className="h-4 w-4 mr-2" />
            Compartilhar...
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ShareCard
        open={cardOpen}
        onClose={() => setCardOpen(false)}
        title={title}
        category={category}
        image={image}
        listingId={listingId}
        shareUrl={fallbackUrl}
      />
    </>
  );
}
